import { useState } from 'react';

/**
 * Ввод количества.
 *
 * Системная клавиатура в цеху не годится: мелкая, перекрывает экран, в перчатках
 * по ней не попасть. Здесь крупные цифры и кнопки быстрого прибавления —
 * основной способ ввода выработки.
 */

export interface NumberPadProps {
  value: number;
  onChange(value: number): void;
  /** Шаг для кнопок быстрого прибавления. */
  quickAdd?: number[];
  /** Дробная часть: для килограммов — один знак. */
  decimals?: 0 | 1;
  label?: string;
  unit?: string;
  max?: number;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function NumberPad({
  value,
  onChange,
  quickAdd = [1, 5, 10, 50],
  decimals = 0,
  label,
  unit,
  max = 9_999_999,
}: NumberPadProps): JSX.Element {
  // Внутри держим строку: иначе ведущий ноль и незаконченное «12,» ведут себя странно
  const [raw, setRaw] = useState<string>(() => formatValue(value, decimals));

  function formatValue(input: number, digits: 0 | 1): string {
    if (digits === 0) return String(input);
    return (input / 10).toFixed(1).replace('.', ',');
  }

  function parse(text: string): number {
    const clean = text.replace(',', '.');
    if (clean === '' || clean === '.') return 0;
    const parsed = Number.parseFloat(clean);
    if (!Number.isFinite(parsed)) return 0;
    return decimals === 0 ? Math.round(parsed) : Math.round(parsed * 10);
  }

  function commit(text: string): void {
    setRaw(text);
    const next = Math.min(parse(text), max);
    onChange(next);
  }

  const press = (key: string): void => {
    if (navigator.vibrate) navigator.vibrate(15);
    if (key === ',' ) {
      if (decimals === 0 || raw.includes(',')) return;
      commit(raw === '' ? '0,' : `${raw},`);
      return;
    }
    const next = raw === '0' ? key : raw + key;
    commit(next);
  };

  const erase = (): void => commit(raw.slice(0, -1));
  const clear = (): void => commit('');

  const add = (delta: number): void => {
    if (navigator.vibrate) navigator.vibrate(15);
    const current = parse(raw);
    const step = decimals === 0 ? delta : delta * 10;
    const next = Math.min(Math.max(current + step, 0), max);
    setRaw(formatValue(next, decimals));
    onChange(next);
  };

  return (
    <div>
      {label && <p className="mb-2 text-base text-body">{label}</p>}

      <div className="mb-3 flex items-baseline justify-end gap-2 rounded-xl bg-canvas px-4 py-3">
        <span className="text-display font-semibold tabular-nums">{raw === '' ? '0' : raw}</span>
        {unit && <span className="text-lg text-dim">{unit}</span>}
      </div>

      <div className="mb-3 grid grid-cols-4 gap-touch">
        {quickAdd.map((delta) => (
          <button
            key={delta}
            type="button"
            onClick={() => add(delta)}
            className="min-h-touch rounded-xl bg-sky-700 text-lg font-semibold text-white active:bg-sky-800"
          >
            +{delta}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-touch">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => press(key)}
            className="min-h-[64px] rounded-2xl bg-muted text-counter font-semibold active:bg-line"
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          onClick={() => (decimals === 1 ? press(',') : clear())}
          className="min-h-[64px] rounded-2xl bg-card shadow-card text-xl font-semibold active:bg-line"
        >
          {decimals === 1 ? ',' : 'Сброс'}
        </button>
        <button
          type="button"
          onClick={() => press('0')}
          className="min-h-[64px] rounded-2xl bg-muted text-counter font-semibold active:bg-line"
        >
          0
        </button>
        <button
          type="button"
          onClick={erase}
          className="min-h-[64px] rounded-2xl bg-card shadow-card text-xl font-semibold active:bg-line"
        >
          ←
        </button>
      </div>
    </div>
  );
}
